import { TextCase } from '../text/TextCase';
import ConfigProvider from '../ConfigProvider';
import NonNumericsRemovalOptions from '../text/NonNumericsRemovalOptions';
import RegExpOptions from './RegExpOptions';
import RegExpUtils from './RegExpUtils';
import StringBuilder from './StringBuilder';
import StringSplitOptions from '../text/StringSplitOptions';
export default class StringUtils {
    static append(str, value, delimiter = '') {
        if (StringUtils.isNullOrEmpty(value)) {
            return str !== null && str !== void 0 ? str : '';
        }
        else if (StringUtils.isNullOrEmpty(str)) {
            return value !== null && value !== void 0 ? value : '';
        }
        else if (delimiter) {
            return str + delimiter + value;
        }
        else {
            return str + value;
        }
    }
    ;
    static appendAffix(str, prefix, value, suffix, delimiter = ' ') {
        if (!value) {
            return str;
        }
        else if (prefix && suffix) {
            return StringUtils.append(str, prefix + value + suffix, delimiter);
        }
        else if (prefix) {
            return StringUtils.append(str, prefix + value, delimiter);
        }
        else if (suffix) {
            return StringUtils.append(str, value + suffix, delimiter);
        }
        else {
            return '';
        }
    }
    ;
    static appendIf(str, condition, value, delimiter = '') {
        if (condition) {
            return StringUtils.append(str, value, delimiter);
        }
        else {
            return str;
        }
    }
    ;
    static between(str, startDelimiter, endDelimiter) {
        let subtringStart = str.indexOf(startDelimiter);
        if (subtringStart === -1) {
            return undefined;
        }
        subtringStart += startDelimiter.length;
        const substringEnd = str.indexOf(endDelimiter, subtringStart);
        if (substringEnd === -1) {
            return undefined;
        }
        const length = substringEnd - subtringStart;
        return str.substr(subtringStart, length);
    }
    ;
    static changeCase(str, textCase) {
        switch (textCase) {
            case TextCase.Lowercase:
                return str.toLocaleLowerCase(ConfigProvider.config.locale);
            case TextCase.Uppercase:
                return str.toLocaleUpperCase(ConfigProvider.config.locale);
            case TextCase.Word:
                return str.toLocaleLowerCase(ConfigProvider.config.locale)
                    .replace(/(^([a-zA-Z\p{M}]))|([ -][a-zA-Z\p{M}])/g, firstLetter => firstLetter.toUpperCase());
            case TextCase.Sentence:
                return str.length > 0 ? (str.charAt(0).toLocaleUpperCase(ConfigProvider.config.locale) + str.slice(1)) : str;
        }
        return str;
    }
    ;
    static chars(str) {
        return str.split('');
    }
    ;
    static compare(str1, str2, caseSensitive = true) {
        if (str1 === undefined && str2 === undefined) {
            return 0;
        }
        else if (str1 === undefined && str2 !== undefined) {
            return -1;
        }
        else if (str1 !== undefined && str2 === undefined) {
            return 1;
        }
        return str1.localeCompare(str2, ConfigProvider.config.locale, { sensitivity: caseSensitive ? 'case' : undefined });
    }
    static contains(str, value, caseSensitive = true) {
        return StringUtils.indexOf(str, value, 0, caseSensitive) !== -1;
    }
    ;
    static containsAny(str, values, caseSensitive = true) {
        return StringUtils.indexOfAny(str, values, 0, caseSensitive) !== -1;
    }
    ;
    static endsWith(str, suffix, caseSensitive = true) {
        if (!caseSensitive) {
            str = str.toLocaleLowerCase(ConfigProvider.config.locale);
        }
        return str.indexOf(suffix) === str.length - suffix.length;
    }
    ;
    static endsWithAny(str, suffixes, caseSensitive = true) {
        for (const suffix of suffixes) {
            if (StringUtils.endsWith(str, suffix, caseSensitive)) {
                return true;
            }
        }
        return false;
    }
    ;
    static equal(str1, str2, caseSensitive = true) {
        return StringUtils.compare(str1, str2, caseSensitive) === 0;
    }
    static forEachMatch(str, pattern, matchCallback, nonMatchCallback, options) {
        options = new RegExpOptions(Object.assign({}, options, { global: true }));
        const regexp = new RegExp(pattern, options.toFlags());
        let match = regexp.exec(str);
        let currentIndex = 0;
        if (!match) {
            nonMatchCallback === null || nonMatchCallback === void 0 ? void 0 : nonMatchCallback(str);
            return;
        }
        else if (match.index > 0) {
            nonMatchCallback === null || nonMatchCallback === void 0 ? void 0 : nonMatchCallback(str.substring(0, match.index));
            if (match[0].length === 0) {
                regexp.lastIndex += 1;
            }
        }
        currentIndex = match.index + match[0].length;
        while (match) {
            matchCallback === null || matchCallback === void 0 ? void 0 : matchCallback(match);
            match = regexp.exec(str);
            if (!match) {
                nonMatchCallback === null || nonMatchCallback === void 0 ? void 0 : nonMatchCallback(str.substring(currentIndex));
            }
            else {
                nonMatchCallback === null || nonMatchCallback === void 0 ? void 0 : nonMatchCallback(str.substring(currentIndex, match.index));
                if (match[0].length === 0) {
                    regexp.lastIndex += 1;
                }
                currentIndex = match.index + match[0].length;
            }
        }
    }
    ;
    static hasMatch(str, pattern, options) {
        if (pattern === undefined) {
            return false;
        }
        options = new RegExpOptions(options);
        return new RegExp(pattern, options.toFlags()).exec(str) !== null;
    }
    ;
    static indexOf(str, value, startIndex = 0, caseSensitive = true) {
        if (value === undefined) {
            return -1;
        }
        else if (caseSensitive) {
            return str.indexOf(value, startIndex);
        }
        else {
            return str.toLocaleLowerCase(ConfigProvider.config.locale)
                .indexOf(value === null || value === void 0 ? void 0 : value.toLocaleLowerCase(ConfigProvider.config.locale));
        }
    }
    static indexOfAny(str, values, startIndex = 0, caseSensitive = true) {
        const indexes = [];
        for (const value of values) {
            const index = StringUtils.indexOf(str, value, startIndex, caseSensitive);
            if (index !== -1) {
                indexes.push(index);
            }
        }
        if (indexes.length === 0) {
            return -1;
        }
        else {
            return Math.min(...indexes);
        }
    }
    ;
    static isHex(str) {
        return str.match(/[0-9a-f]+$/i) !== null;
    }
    ;
    static isNumeric(str) {
        return str.match(/\d+$/) !== null;
    }
    ;
    static lastIndexOf(str, value, startIndex = 0, caseSensitive = true) {
        if (value === undefined) {
            return -1;
        }
        else if (caseSensitive) {
            return str.lastIndexOf(value, startIndex);
        }
        else {
            return str.toLocaleLowerCase(ConfigProvider.config.locale)
                .lastIndexOf(value === null || value === void 0 ? void 0 : value.toLocaleLowerCase(ConfigProvider.config.locale));
        }
    }
    static lastIndexOfAny(str, values, startIndex = 0, caseSensitive = true) {
        const indexes = [];
        for (const value of values) {
            const index = StringUtils.lastIndexOf(str, value, startIndex, caseSensitive);
            if (index !== -1) {
                indexes.push(index);
            }
        }
        if (indexes.length === 0) {
            return -1;
        }
        else {
            return Math.max(...indexes);
        }
    }
    ;
    static matchAll(str, pattern, options) {
        options = new RegExpOptions(Object.assign({}, options, { global: true }));
        const result = [];
        const regexp = new RegExp(pattern, options.toFlags());
        let match = regexp.exec(str);
        do {
            if (match) {
                result.push(match);
            }
            match = regexp.exec(str);
        } while (match);
        return result;
    }
    ;
    static matchFirst(str, pattern, options) {
        var _a;
        options = new RegExpOptions(options);
        return (_a = RegExp(pattern, options.toFlags()).exec(str)) !== null && _a !== void 0 ? _a : undefined;
    }
    ;
    static matchAndReplaceAll(str, pattern, replaceValue, options) {
        options = new RegExpOptions(Object.assign({}, options, { global: true }));
        const buffer = new StringBuilder(str.length);
        StringUtils.forEachMatch(str, pattern, (match) => {
            if (typeof replaceValue === 'function') {
                buffer.append(replaceValue(match));
            }
            else {
                buffer.append(replaceValue);
            }
        }, (nonMatch) => buffer.append(nonMatch), options);
        return buffer.toString();
    }
    ;
    static padLeft(str, width, value = '0') {
        return str.length >= width ? str : (new Array(width - str.length + 1).join(value) + str);
    }
    ;
    static padRight(str, width, value = '0') {
        return str.length >= width ? str : str + (new Array(width - str.length + 1).join(value));
    }
    ;
    static removeAll(str, value, caseSensitive = true) {
        return StringUtils.matchAndReplaceAll(str, RegExpUtils.escape(value), '', { caseSensitive: caseSensitive });
    }
    ;
    static removeAllMatches(str, pattern, options) {
        return StringUtils.matchAndReplaceAll(str, pattern, '', options);
    }
    ;
    static removeAt(str, index, length) {
        if (length === undefined) {
            return str.substr(0, index);
        }
        else {
            return str.substr(0, index) + str.substr(index + length);
        }
    }
    ;
    static removeBetween(str, startDelimiter, endDelimiter, removeDelimiters = false) {
        let subtringStart = str.indexOf(startDelimiter);
        if (subtringStart === -1) {
            return str;
        }
        subtringStart += startDelimiter.length;
        const substringEnd = str.indexOf(endDelimiter, subtringStart);
        if (substringEnd === -1) {
            return str;
        }
        const before = str.substr(0, subtringStart - (removeDelimiters ? startDelimiter.length : 0));
        const after = str.substr(substringEnd + (removeDelimiters ? endDelimiter.length : 0));
        return before + after;
    }
    ;
    static removeLeft(str, width) {
        return str.substr(width);
    }
    ;
    static removeNonNumerics(str, options) {
        options = new NonNumericsRemovalOptions(options);
        const allowedCharactersRegEx = '0-9'
            .concat(options.allowNegativeSign ? ('\\' + options.negativeSign) : '')
            .concat(options.allowGroupSeparator ? ('\\' + options.groupSeparator) : '')
            .concat(options.allowDecimalSeparator ? ('\\' + options.decimalSeparator) : '');
        const regExp = new RegExp(`[^${allowedCharactersRegEx}]+`, 'g');
        return str.replace(regExp, '');
    }
    ;
    static removeNonAlphabetics(str) {
        return str.replace(/[^a-zA-Z]+/g, '');
    }
    ;
    static removeNonAlphanumerics(str) {
        return str.replace(/[^a-zA-Z0-9]+/g, '');
    }
    ;
    static removeRange(str, startIndex, endIndexExclusive) {
        return StringUtils.removeAt(str, startIndex, endIndexExclusive - startIndex);
    }
    ;
    static replaceAll(str, searchValue, replaceValue) {
        return str.replace(new RegExp(RegExpUtils.escape(searchValue), 'g'), replaceValue);
    }
    ;
    static reverse(str) {
        return str.split('').reverse().join('');
    }
    ;
    static split(str, delimiter, options) {
        options = new StringSplitOptions(options);
        let delimiters;
        if (StringUtils.isString(delimiter)) {
            delimiters = [delimiter];
        }
        else {
            delimiters = delimiter;
        }
        const regExp = delimiters.map(s => RegExpUtils.escape(s)).join('|');
        let result = str.split(new RegExp(regExp));
        if (options.trimEntries) {
            result = result.map(s => s.trim());
        }
        if (options.removeEmptyEntries) {
            result = result.filter(s => !StringUtils.isNullOrEmpty(s));
        }
        return result;
    }
    ;
    static splitLines(str, options) {
        return StringUtils.split(str, ['\r', '\n'], options);
    }
    ;
    static splitMatch(str, pattern, options) {
        options = new StringSplitOptions(options);
        let result = [];
        StringUtils.forEachMatch(str, pattern, undefined, (nonMatch) => result.push(nonMatch), options);
        if (options.trimEntries) {
            result = result.map(s => s.trim());
        }
        if (options.removeEmptyEntries) {
            result = result.filter(s => !StringUtils.isNullOrEmpty(s));
        }
        return result;
    }
    static startsWith(str, prefix, caseSensitive = true) {
        if (!caseSensitive) {
            str = str.toLocaleLowerCase(ConfigProvider.config.locale);
        }
        return str.indexOf(prefix) === 0;
    }
    ;
    static startsWithAny(str, prefixes, caseSensitive = true) {
        for (const prefix of prefixes) {
            if (StringUtils.startsWith(str, prefix, caseSensitive)) {
                return true;
            }
        }
        return false;
    }
    ;
    static trimLeft(str, characters, maxTrimWidth) {
        let startIndex = 0;
        const endIndex = Math.min(maxTrimWidth !== null && maxTrimWidth !== void 0 ? maxTrimWidth : str.length, str.length);
        while (startIndex < endIndex && characters.split('').find(c => c === str[startIndex])) {
            startIndex++;
        }
        return str.substr(startIndex);
    }
    ;
    static trimRight(str, characters, maxTrimWidth) {
        let endIndex = str.length - 1;
        const startIndex = Math.max(str.length - (maxTrimWidth !== null && maxTrimWidth !== void 0 ? maxTrimWidth : 0), 0);
        while (endIndex >= startIndex && characters.split('').find(c => c === str[endIndex])) {
            endIndex--;
        }
        return str.substr(0, endIndex + 1);
    }
    ;
    static trim(str, characters, maxTrimWidth) {
        return StringUtils.trimRight(StringUtils.trimLeft(str, characters, maxTrimWidth), characters, maxTrimWidth);
    }
    ;
    static trimWithEllipsis(str, maxLength) {
        if (str.length > maxLength) {
            return str.substring(0, maxLength - 1) + '...';
        }
        else {
            return str;
        }
    }
    ;
}
StringUtils.emptyIfNull = function (value) {
    return !value ? '' : value;
};
StringUtils.isNullOrEmpty = function (value) {
    return !value;
};
StringUtils.isNullOrWhiteSpace = function (value) {
    return !value || !value.trim();
};
StringUtils.isString = function (obj) {
    return typeof obj === 'string' || obj instanceof String;
};
//# sourceMappingURL=StringUtils.js.map